// Question Service
// Provides questions filtered by category for personality analysis.
// Questions now live in the database (heart_game_questions); the hardcoded
// set below is retained only as an offline/fallback safety net.

import { MBTIMapping } from '../../types/heartGames';
import { config } from '../../config/env';
import { authInterceptor } from '../Authentication/authInterceptor';

const API_BASE = `${config.api.baseUrl}/heart-games`;

export interface QuestionCard {
  id: string;
  question: string;
  options: Array<{
    id: string;
    text: string;
    mbtiWeight: MBTIMapping;
  }>;
  category: string;
}

export class QuestionService {
  // Load questions for a category from the backend (source of truth), falling
  // back to the bundled set if the request fails or returns nothing.
  static async fetchQuestionsByCategory(category: string, count: number = 10): Promise<QuestionCard[]> {
    try {
      const response = await fetch(`${API_BASE}/questions?category=${encodeURIComponent(category)}`, {
        headers: { Authorization: `Bearer ${authInterceptor.getAccessToken() || ''}` },
      });
      if (!response.ok) throw new Error(`Request failed: ${response.statusText}`);
      const { data } = await response.json();
      const mapped: QuestionCard[] = (data ?? []).map((row: any) => ({
        id: row.id,
        question: row.question_text,
        options: row.options,
        category: row.category,
      }));
      if (mapped.length === 0) throw new Error('No questions returned');
      return mapped.slice(0, count);
    } catch (error) {
      console.error('Error fetching questions, falling back to bundled set:', error);
      return this.getQuestionsByCategory(category, count);
    }
  }

  // Bundled fallback set (formerly the only source).
  static getQuestionsByCategory(category: string, count: number = 10): QuestionCard[] {
    const allQuestions = this.getAllQuestions();
    return allQuestions
      .filter(q => q.category === category)
      .slice(0, count);
  }

  // Get all available questions (mock data for now)
  private static getAllQuestions(): QuestionCard[] {
    // Dating category questions
    const datingQuestions: QuestionCard[] = [
      {
        id: 'dating-1',
        question: 'On a first date, you prefer to:',
        options: [
          {
            id: 'a',
            text: 'Go to a quiet coffee shop for deep conversation',
            mbtiWeight: { E: 0, I: 1, S: 0, N: 1, T: 0, F: 1, J: 0, P: 0 }
          },
          {
            id: 'b',
            text: 'Try an adventurous activity together',
            mbtiWeight: { E: 1, I: 0, S: 1, N: 0, T: 0, F: 0, J: 0, P: 1 }
          },
          {
            id: 'c',
            text: 'Have a structured dinner at a nice restaurant',
            mbtiWeight: { E: 1, I: 0, S: 1, N: 0, T: 0, F: 0, J: 1, P: 0 }
          },
          {
            id: 'd',
            text: 'Explore a new neighborhood together',
            mbtiWeight: { E: 1, I: 0, S: 0, N: 1, T: 0, F: 0, J: 0, P: 1 }
          }
        ],
        category: 'dating'
      },
      {
        id: 'dating-2',
        question: 'When texting someone you like, you:',
        options: [
          {
            id: 'a',
            text: 'Respond immediately and keep the conversation going',
            mbtiWeight: { E: 1, I: 0, S: 0, N: 0, T: 0, F: 1, J: 0, P: 0 }
          },
          {
            id: 'b',
            text: 'Take time to craft thoughtful responses',
            mbtiWeight: { E: 0, I: 1, S: 0, N: 1, T: 0, F: 1, J: 1, P: 0 }
          },
          {
            id: 'c',
            text: 'Keep it brief and to the point',
            mbtiWeight: { E: 0, I: 1, S: 1, N: 0, T: 1, F: 0, J: 1, P: 0 }
          },
          {
            id: 'd',
            text: 'Use lots of emojis and keep it fun',
            mbtiWeight: { E: 1, I: 0, S: 1, N: 0, T: 0, F: 1, J: 0, P: 1 }
          }
        ],
        category: 'dating'
      },
      {
        id: 'dating-3',
        question: 'Your ideal relationship communication style is:',
        options: [
          {
            id: 'a',
            text: 'Open and frequent conversations about everything',
            mbtiWeight: { E: 1, I: 0, S: 0, N: 1, T: 0, F: 1, J: 0, P: 0 }
          },
          {
            id: 'b',
            text: 'Quality over quantity - meaningful discussions',
            mbtiWeight: { E: 0, I: 1, S: 0, N: 1, T: 0, F: 1, J: 1, P: 0 }
          },
          {
            id: 'c',
            text: 'Practical and solution-focused talks',
            mbtiWeight: { E: 0, I: 1, S: 1, N: 0, T: 1, F: 0, J: 1, P: 0 }
          },
          {
            id: 'd',
            text: 'Spontaneous and playful exchanges',
            mbtiWeight: { E: 1, I: 0, S: 1, N: 0, T: 0, F: 0, J: 0, P: 1 }
          }
        ],
        category: 'dating'
      },
      {
        id: 'dating-4',
        question: 'When planning a date, you prefer:',
        options: [
          {
            id: 'a',
            text: 'Letting things happen spontaneously',
            mbtiWeight: { E: 1, I: 0, S: 0, N: 1, T: 0, F: 0, J: 0, P: 1 }
          },
          {
            id: 'b',
            text: 'Creating a detailed itinerary',
            mbtiWeight: { E: 0, I: 1, S: 1, N: 0, T: 1, F: 0, J: 1, P: 0 }
          },
          {
            id: 'c',
            text: 'Discussing options together',
            mbtiWeight: { E: 1, I: 0, S: 1, N: 0, T: 0, F: 1, J: 0, P: 0 }
          },
          {
            id: 'd',
            text: 'Having a few backup plans ready',
            mbtiWeight: { E: 0, I: 1, S: 0, N: 1, T: 1, F: 0, J: 1, P: 0 }
          }
        ],
        category: 'dating'
      },
      {
        id: 'dating-5',
        question: 'In a relationship, you value most:',
        options: [
          {
            id: 'a',
            text: 'Emotional connection and understanding',
            mbtiWeight: { E: 0, I: 0, S: 0, N: 1, T: 0, F: 1, J: 0, P: 0 }
          },
          {
            id: 'b',
            text: 'Shared activities and experiences',
            mbtiWeight: { E: 1, I: 0, S: 1, N: 0, T: 0, F: 0, J: 0, P: 1 }
          },
          {
            id: 'c',
            text: 'Stability and reliability',
            mbtiWeight: { E: 0, I: 1, S: 1, N: 0, T: 1, F: 0, J: 1, P: 0 }
          },
          {
            id: 'd',
            text: 'Intellectual stimulation and growth',
            mbtiWeight: { E: 1, I: 1, S: 0, N: 1, T: 1, F: 0, J: 0, P: 0 }
          }
        ],
        category: 'dating'
      },
      {
        id: 'dating-6',
        question: 'When meeting someone new, your first impression is based on:',
        options: [
          {
            id: 'a',
            text: 'Their energy and how they make you feel',
            mbtiWeight: { E: 1, I: 0, S: 0, N: 1, T: 0, F: 1, J: 0, P: 0 }
          },
          {
            id: 'b',
            text: 'Their appearance and body language',
            mbtiWeight: { E: 0, I: 1, S: 1, N: 0, T: 1, F: 0, J: 1, P: 0 }
          },
          {
            id: 'c',
            text: 'Their conversation style and what they say',
            mbtiWeight: { E: 0, I: 1, S: 0, N: 1, T: 1, F: 0, J: 1, P: 0 }
          },
          {
            id: 'd',
            text: 'The potential for an interesting connection',
            mbtiWeight: { E: 1, I: 0, S: 0, N: 1, T: 0, F: 1, J: 0, P: 1 }
          }
        ],
        category: 'dating'
      },
      {
        id: 'dating-7',
        question: 'If your partner cancels plans last minute, you:',
        options: [
          {
            id: 'a',
            text: 'Feel disappointed but understand things happen',
            mbtiWeight: { E: 0, I: 1, S: 1, N: 0, T: 0, F: 1, J: 1, P: 0 }
          },
          {
            id: 'b',
            text: 'Ask what happened and offer to help',
            mbtiWeight: { E: 1, I: 0, S: 1, N: 0, T: 0, F: 1, J: 0, P: 0 }
          },
          {
            id: 'c',
            text: 'Feel hurt and need to discuss it',
            mbtiWeight: { E: 0, I: 1, S: 0, N: 1, T: 0, F: 1, J: 1, P: 0 }
          },
          {
            id: 'd',
            text: 'Quickly make alternative plans',
            mbtiWeight: { E: 1, I: 0, S: 0, N: 1, T: 1, F: 0, J: 0, P: 1 }
          }
        ],
        category: 'dating'
      },
      {
        id: 'dating-8',
        question: 'Your ideal way to spend Valentine\'s Day is:',
        options: [
          {
            id: 'a',
            text: 'A quiet, intimate dinner at home',
            mbtiWeight: { E: 0, I: 1, S: 1, N: 0, T: 0, F: 1, J: 1, P: 0 }
          },
          {
            id: 'b',
            text: 'A surprise adventure or activity',
            mbtiWeight: { E: 1, I: 0, S: 0, N: 1, T: 0, F: 1, J: 0, P: 1 }
          },
          {
            id: 'c',
            text: 'A well-planned romantic evening',
            mbtiWeight: { E: 1, I: 0, S: 1, N: 0, T: 1, F: 0, J: 1, P: 0 }
          },
          {
            id: 'd',
            text: 'Something unique and meaningful to both of you',
            mbtiWeight: { E: 0, I: 1, S: 0, N: 1, T: 0, F: 1, J: 0, P: 0 }
          }
        ],
        category: 'dating'
      },
      {
        id: 'dating-9',
        question: 'When you have a disagreement with your partner, you:',
        options: [
          {
            id: 'a',
            text: 'Want to talk it out immediately',
            mbtiWeight: { E: 1, I: 0, S: 1, N: 0, T: 1, F: 0, J: 1, P: 0 }
          },
          {
            id: 'b',
            text: 'Need time alone to process your feelings',
            mbtiWeight: { E: 0, I: 1, S: 0, N: 1, T: 0, F: 1, J: 1, P: 0 }
          },
          {
            id: 'c',
            text: 'Focus on finding a practical solution',
            mbtiWeight: { E: 0, I: 1, S: 1, N: 0, T: 1, F: 0, J: 1, P: 0 }
          },
          {
            id: 'd',
            text: 'Try to understand their perspective first',
            mbtiWeight: { E: 1, I: 0, S: 0, N: 1, T: 0, F: 1, J: 0, P: 0 }
          }
        ],
        category: 'dating'
      },
      {
        id: 'dating-10',
        question: 'You prefer to meet potential partners:',
        options: [
          {
            id: 'a',
            text: 'Through friends or social events',
            mbtiWeight: { E: 1, I: 0, S: 1, N: 0, T: 0, F: 1, J: 0, P: 0 }
          },
          {
            id: 'b',
            text: 'Through shared interests or hobbies',
            mbtiWeight: { E: 0, I: 1, S: 1, N: 0, T: 1, F: 0, J: 1, P: 0 }
          },
          {
            id: 'c',
            text: 'Through dating apps where you can filter',
            mbtiWeight: { E: 0, I: 1, S: 0, N: 1, T: 1, F: 0, J: 1, P: 0 }
          },
          {
            id: 'd',
            text: 'Naturally, through everyday interactions',
            mbtiWeight: { E: 1, I: 0, S: 0, N: 1, T: 0, F: 1, J: 0, P: 1 }
          }
        ],
        category: 'dating'
      },
      {
        id: 'dating-11',
        question: 'When your partner is stressed, you:',
        options: [
          {
            id: 'a',
            text: 'Offer emotional support and listen',
            mbtiWeight: { E: 0, I: 1, S: 0, N: 1, T: 0, F: 1, J: 0, P: 0 }
          },
          {
            id: 'b',
            text: 'Try to help solve the problem',
            mbtiWeight: { E: 1, I: 0, S: 1, N: 0, T: 1, F: 0, J: 1, P: 0 }
          },
          {
            id: 'c',
            text: 'Suggest activities to take their mind off it',
            mbtiWeight: { E: 1, I: 0, S: 1, N: 0, T: 0, F: 1, J: 0, P: 1 }
          },
          {
            id: 'd',
            text: 'Give them space and check in later',
            mbtiWeight: { E: 0, I: 1, S: 1, N: 0, T: 1, F: 0, J: 1, P: 0 }
          }
        ],
        category: 'dating'
      },
      {
        id: 'dating-12',
        question: 'Your deal-breaker in a relationship is:',
        options: [
          {
            id: 'a',
            text: 'Lack of emotional intelligence or empathy',
            mbtiWeight: { E: 0, I: 0, S: 0, N: 1, T: 0, F: 1, J: 0, P: 0 }
          },
          {
            id: 'b',
            text: 'Unreliability or flakiness',
            mbtiWeight: { E: 0, I: 1, S: 1, N: 0, T: 1, F: 0, J: 1, P: 0 }
          },
          {
            id: 'c',
            text: 'Lack of ambition or goals',
            mbtiWeight: { E: 1, I: 1, S: 1, N: 0, T: 1, F: 0, J: 1, P: 0 }
          },
          {
            id: 'd',
            text: 'Being too rigid or controlling',
            mbtiWeight: { E: 1, I: 0, S: 0, N: 1, T: 0, F: 0, J: 0, P: 1 }
          }
        ],
        category: 'dating'
      },
      {
        id: 'dating-13',
        question: 'When introducing your partner to friends, you:',
        options: [
          {
            id: 'a',
            text: 'Plan a group gathering to see how they interact',
            mbtiWeight: { E: 1, I: 0, S: 1, N: 0, T: 0, F: 1, J: 1, P: 0 }
          },
          {
            id: 'b',
            text: 'Prefer one-on-one introductions',
            mbtiWeight: { E: 0, I: 1, S: 0, N: 1, T: 0, F: 1, J: 0, P: 0 }
          },
          {
            id: 'c',
            text: 'Let it happen naturally in social settings',
            mbtiWeight: { E: 1, I: 0, S: 0, N: 1, T: 0, F: 0, J: 0, P: 1 }
          },
          {
            id: 'd',
            text: 'Wait until you\'re sure about the relationship',
            mbtiWeight: { E: 0, I: 1, S: 1, N: 0, T: 1, F: 0, J: 1, P: 0 }
          }
        ],
        category: 'dating'
      },
      {
        id: 'dating-14',
        question: 'Your approach to physical intimacy is:',
        options: [
          {
            id: 'a',
            text: 'It should develop naturally with emotional connection',
            mbtiWeight: { E: 0, I: 1, S: 0, N: 1, T: 0, F: 1, J: 0, P: 0 }
          },
          {
            id: 'b',
            text: 'Important for building connection early on',
            mbtiWeight: { E: 1, I: 0, S: 1, N: 0, T: 0, F: 1, J: 0, P: 1 }
          },
          {
            id: 'c',
            text: 'Something to discuss and plan together',
            mbtiWeight: { E: 0, I: 1, S: 1, N: 0, T: 1, F: 0, J: 1, P: 0 }
          },
          {
            id: 'd',
            text: 'Spontaneous and fun',
            mbtiWeight: { E: 1, I: 0, S: 0, N: 1, T: 0, F: 0, J: 0, P: 1 }
          }
        ],
        category: 'dating'
      },
      {
        id: 'dating-15',
        question: 'When your partner shares a problem, you:',
        options: [
          {
            id: 'a',
            text: 'Listen and offer emotional validation',
            mbtiWeight: { E: 0, I: 1, S: 0, N: 1, T: 0, F: 1, J: 0, P: 0 }
          },
          {
            id: 'b',
            text: 'Immediately think of solutions to suggest',
            mbtiWeight: { E: 1, I: 0, S: 1, N: 0, T: 1, F: 0, J: 1, P: 0 }
          },
          {
            id: 'c',
            text: 'Ask questions to understand the full situation',
            mbtiWeight: { E: 0, I: 1, S: 1, N: 0, T: 1, F: 0, J: 1, P: 0 }
          },
          {
            id: 'd',
            text: 'Share a similar experience to relate',
            mbtiWeight: { E: 1, I: 0, S: 0, N: 1, T: 0, F: 1, J: 0, P: 1 }
          }
        ],
        category: 'dating'
      },
      {
        id: 'dating-16',
        question: 'You prefer relationships where:',
        options: [
          {
            id: 'a',
            text: 'You maintain your independence and personal space',
            mbtiWeight: { E: 0, I: 1, S: 0, N: 1, T: 1, F: 0, J: 0, P: 1 }
          },
          {
            id: 'b',
            text: 'You do most things together as a couple',
            mbtiWeight: { E: 1, I: 0, S: 1, N: 0, T: 0, F: 1, J: 0, P: 0 }
          },
          {
            id: 'c',
            text: 'You have clear roles and expectations',
            mbtiWeight: { E: 0, I: 1, S: 1, N: 0, T: 1, F: 0, J: 1, P: 0 }
          },
          {
            id: 'd',
            text: 'You grow and explore life together',
            mbtiWeight: { E: 1, I: 0, S: 0, N: 1, T: 0, F: 1, J: 0, P: 1 }
          }
        ],
        category: 'dating'
      },
      {
        id: 'dating-17',
        question: 'When planning a future together, you:',
        options: [
          {
            id: 'a',
            text: 'Create detailed timelines and goals',
            mbtiWeight: { E: 0, I: 1, S: 1, N: 0, T: 1, F: 0, J: 1, P: 0 }
          },
          {
            id: 'b',
            text: 'Discuss possibilities and dreams',
            mbtiWeight: { E: 1, I: 0, S: 0, N: 1, T: 0, F: 1, J: 0, P: 1 }
          },
          {
            id: 'c',
            text: 'Focus on building a stable foundation first',
            mbtiWeight: { E: 0, I: 1, S: 1, N: 0, T: 1, F: 0, J: 1, P: 0 }
          },
          {
            id: 'd',
            text: 'Take it step by step as opportunities arise',
            mbtiWeight: { E: 1, I: 0, S: 0, N: 1, T: 0, F: 0, J: 0, P: 1 }
          }
        ],
        category: 'dating'
      },
      {
        id: 'dating-18',
        question: 'Your ideal partner\'s communication style is:',
        options: [
          {
            id: 'a',
            text: 'Open and expressive about feelings',
            mbtiWeight: { E: 1, I: 0, S: 0, N: 1, T: 0, F: 1, J: 0, P: 0 }
          },
          {
            id: 'b',
            text: 'Thoughtful and selective with words',
            mbtiWeight: { E: 0, I: 1, S: 0, N: 1, T: 1, F: 0, J: 1, P: 0 }
          },
          {
            id: 'c',
            text: 'Direct and straightforward',
            mbtiWeight: { E: 0, I: 1, S: 1, N: 0, T: 1, F: 0, J: 1, P: 0 }
          },
          {
            id: 'd',
            text: 'Playful and spontaneous',
            mbtiWeight: { E: 1, I: 0, S: 1, N: 0, T: 0, F: 1, J: 0, P: 1 }
          }
        ],
        category: 'dating'
      },
      {
        id: 'dating-19',
        question: 'When you\'re upset with your partner, you:',
        options: [
          {
            id: 'a',
            text: 'Express it immediately to clear the air',
            mbtiWeight: { E: 1, I: 0, S: 1, N: 0, T: 1, F: 0, J: 1, P: 0 }
          },
          {
            id: 'b',
            text: 'Withdraw and process your feelings alone first',
            mbtiWeight: { E: 0, I: 1, S: 0, N: 1, T: 0, F: 1, J: 1, P: 0 }
          },
          {
            id: 'c',
            text: 'Try to understand why you feel that way',
            mbtiWeight: { E: 0, I: 1, S: 0, N: 1, T: 0, F: 1, J: 0, P: 0 }
          },
          {
            id: 'd',
            text: 'Wait for the right moment to discuss it',
            mbtiWeight: { E: 1, I: 0, S: 1, N: 0, T: 0, F: 1, J: 1, P: 0 }
          }
        ],
        category: 'dating'
      },
      {
        id: 'dating-20',
        question: 'You show love by:',
        options: [
          {
            id: 'a',
            text: 'Spending quality time and having deep conversations',
            mbtiWeight: { E: 0, I: 1, S: 0, N: 1, T: 0, F: 1, J: 0, P: 0 }
          },
          {
            id: 'b',
            text: 'Doing things for them and helping with tasks',
            mbtiWeight: { E: 0, I: 1, S: 1, N: 0, T: 1, F: 0, J: 1, P: 0 }
          },
          {
            id: 'c',
            text: 'Physical affection and being together',
            mbtiWeight: { E: 1, I: 0, S: 1, N: 0, T: 0, F: 1, J: 0, P: 0 }
          },
          {
            id: 'd',
            text: 'Surprising them with thoughtful gestures',
            mbtiWeight: { E: 1, I: 0, S: 0, N: 1, T: 0, F: 1, J: 0, P: 1 }
          }
        ],
        category: 'dating'
      },
      {
        id: 'dating-21',
        question: 'Your approach to meeting your partner\'s family is:',
        options: [
          {
            id: 'a',
            text: 'Nervous but excited to make a good impression',
            mbtiWeight: { E: 0, I: 1, S: 1, N: 0, T: 0, F: 1, J: 1, P: 0 }
          },
          {
            id: 'b',
            text: 'Confident and ready to engage with everyone',
            mbtiWeight: { E: 1, I: 0, S: 1, N: 0, T: 0, F: 1, J: 0, P: 0 }
          },
          {
            id: 'c',
            text: 'Prefer one-on-one meetings first',
            mbtiWeight: { E: 0, I: 1, S: 0, N: 1, T: 1, F: 0, J: 1, P: 0 }
          },
          {
            id: 'd',
            text: 'Go with the flow and see how it unfolds',
            mbtiWeight: { E: 1, I: 0, S: 0, N: 1, T: 0, F: 0, J: 0, P: 1 }
          }
        ],
        category: 'dating'
      },
      {
        id: 'dating-22',
        question: 'When your partner needs space, you:',
        options: [
          {
            id: 'a',
            text: 'Respect it but check in to make sure they\'re okay',
            mbtiWeight: { E: 1, I: 0, S: 1, N: 0, T: 0, F: 1, J: 0, P: 0 }
          },
          {
            id: 'b',
            text: 'Give them complete space without questions',
            mbtiWeight: { E: 0, I: 1, S: 1, N: 0, T: 1, F: 0, J: 1, P: 0 }
          },
          {
            id: 'c',
            text: 'Feel worried and want to understand why',
            mbtiWeight: { E: 0, I: 1, S: 0, N: 1, T: 0, F: 1, J: 1, P: 0 }
          },
          {
            id: 'd',
            text: 'Use the time to focus on your own interests',
            mbtiWeight: { E: 1, I: 1, S: 0, N: 1, T: 1, F: 0, J: 0, P: 1 }
          }
        ],
        category: 'dating'
      },
      {
        id: 'dating-23',
        question: 'Your ideal relationship milestone timeline is:',
        options: [
          {
            id: 'a',
            text: 'Moving slowly and letting things develop naturally',
            mbtiWeight: { E: 0, I: 1, S: 0, N: 1, T: 0, F: 1, J: 0, P: 1 }
          },
          {
            id: 'b',
            text: 'Following a traditional progression',
            mbtiWeight: { E: 0, I: 1, S: 1, N: 0, T: 1, F: 0, J: 1, P: 0 }
          },
          {
            id: 'c',
            text: 'Moving quickly when it feels right',
            mbtiWeight: { E: 1, I: 0, S: 0, N: 1, T: 0, F: 1, J: 0, P: 1 }
          },
          {
            id: 'd',
            text: 'Discussing and planning milestones together',
            mbtiWeight: { E: 1, I: 0, S: 1, N: 0, T: 1, F: 0, J: 1, P: 0 }
          }
        ],
        category: 'dating'
      },
      {
        id: 'dating-24',
        question: 'When your partner has different interests, you:',
        options: [
          {
            id: 'a',
            text: 'Try to learn about and participate in their interests',
            mbtiWeight: { E: 1, I: 0, S: 1, N: 0, T: 0, F: 1, J: 0, P: 0 }
          },
          {
            id: 'b',
            text: 'Support them but maintain your own separate interests',
            mbtiWeight: { E: 0, I: 1, S: 1, N: 0, T: 1, F: 0, J: 1, P: 0 }
          },
          {
            id: 'c',
            text: 'Find common ground and shared activities',
            mbtiWeight: { E: 1, I: 0, S: 0, N: 1, T: 0, F: 1, J: 0, P: 1 }
          },
          {
            id: 'd',
            text: 'Appreciate the diversity it brings to your relationship',
            mbtiWeight: { E: 0, I: 1, S: 0, N: 1, T: 1, F: 0, J: 0, P: 0 }
          }
        ],
        category: 'dating'
      },
      {
        id: 'dating-25',
        question: 'Your biggest relationship fear is:',
        options: [
          {
            id: 'a',
            text: 'Losing your independence or being controlled',
            mbtiWeight: { E: 1, I: 1, S: 0, N: 1, T: 1, F: 0, J: 0, P: 1 }
          },
          {
            id: 'b',
            text: 'Not being truly understood or accepted',
            mbtiWeight: { E: 0, I: 1, S: 0, N: 1, T: 0, F: 1, J: 1, P: 0 }
          },
          {
            id: 'c',
            text: 'Being taken for granted or not appreciated',
            mbtiWeight: { E: 1, I: 0, S: 1, N: 0, T: 0, F: 1, J: 0, P: 0 }
          },
          {
            id: 'd',
            text: 'The relationship becoming stagnant or boring',
            mbtiWeight: { E: 1, I: 0, S: 0, N: 1, T: 0, F: 0, J: 0, P: 1 }
          }
        ],
        category: 'dating'
      }
    ];

    // Lifestyle category questions
    const lifestyleQuestions: QuestionCard[] = [
      {
        id: 'lifestyle-1',
        question: 'Your ideal weekend involves:',
        options: [
          {
            id: 'a',
            text: 'Relaxing at home with a good book or movie',
            mbtiWeight: { E: 0, I: 1, S: 0, N: 1, T: 0, F: 0, J: 0, P: 0 }
          },
          {
            id: 'b',
            text: 'Socializing with friends at events or parties',
            mbtiWeight: { E: 1, I: 0, S: 1, N: 0, T: 0, F: 1, J: 0, P: 0 }
          },
          {
            id: 'c',
            text: 'Trying new activities or exploring new places',
            mbtiWeight: { E: 1, I: 0, S: 0, N: 1, T: 0, F: 0, J: 0, P: 1 }
          },
          {
            id: 'd',
            text: 'Completing planned projects or organizing',
            mbtiWeight: { E: 0, I: 1, S: 1, N: 0, T: 1, F: 0, J: 1, P: 0 }
          }
        ],
        category: 'lifestyle'
      },
      {
        id: 'lifestyle-2',
        question: 'When it comes to your living space, you prefer:',
        options: [
          {
            id: 'a',
            text: 'Minimal and organized',
            mbtiWeight: { E: 0, I: 1, S: 1, N: 0, T: 1, F: 0, J: 1, P: 0 }
          },
          {
            id: 'b',
            text: 'Cozy and inviting for guests',
            mbtiWeight: { E: 1, I: 0, S: 1, N: 0, T: 0, F: 1, J: 0, P: 0 }
          },
          {
            id: 'c',
            text: 'Creative and expressive',
            mbtiWeight: { E: 1, I: 1, S: 0, N: 1, T: 0, F: 1, J: 0, P: 1 }
          },
          {
            id: 'd',
            text: 'Functional and practical',
            mbtiWeight: { E: 0, I: 1, S: 1, N: 0, T: 1, F: 0, J: 1, P: 0 }
          }
        ],
        category: 'lifestyle'
      },
      {
        id: 'lifestyle-3',
        question: 'Your approach to health and fitness is:',
        options: [
          {
            id: 'a',
            text: 'Structured routine with clear goals',
            mbtiWeight: { E: 0, I: 1, S: 1, N: 0, T: 1, F: 0, J: 1, P: 0 }
          },
          {
            id: 'b',
            text: 'Social activities like group classes',
            mbtiWeight: { E: 1, I: 0, S: 1, N: 0, T: 0, F: 1, J: 0, P: 0 }
          },
          {
            id: 'c',
            text: 'Varied and experimental',
            mbtiWeight: { E: 1, I: 0, S: 0, N: 1, T: 0, F: 0, J: 0, P: 1 }
          },
          {
            id: 'd',
            text: 'Mindful and holistic',
            mbtiWeight: { E: 0, I: 1, S: 0, N: 1, T: 0, F: 1, J: 1, P: 0 }
          }
        ],
        category: 'lifestyle'
      },
      {
        id: 'lifestyle-4',
        question: 'When it comes to travel, you prefer:',
        options: [
          {
            id: 'a',
            text: 'Detailed itineraries and planned activities',
            mbtiWeight: { E: 0, I: 1, S: 1, N: 0, T: 1, F: 0, J: 1, P: 0 }
          },
          {
            id: 'b',
            text: 'Spontaneous adventures and discoveries',
            mbtiWeight: { E: 1, I: 0, S: 0, N: 1, T: 0, F: 0, J: 0, P: 1 }
          },
          {
            id: 'c',
            text: 'Group trips with friends',
            mbtiWeight: { E: 1, I: 0, S: 1, N: 0, T: 0, F: 1, J: 0, P: 0 }
          },
          {
            id: 'd',
            text: 'Solo journeys for reflection',
            mbtiWeight: { E: 0, I: 1, S: 0, N: 1, T: 0, F: 1, J: 1, P: 0 }
          }
        ],
        category: 'lifestyle'
      },
      {
        id: 'lifestyle-5',
        question: 'Your work style is best described as:',
        options: [
          {
            id: 'a',
            text: 'Collaborative and team-oriented',
            mbtiWeight: { E: 1, I: 0, S: 1, N: 0, T: 0, F: 1, J: 0, P: 0 }
          },
          {
            id: 'b',
            text: 'Independent and focused',
            mbtiWeight: { E: 0, I: 1, S: 0, N: 1, T: 1, F: 0, J: 1, P: 0 }
          },
          {
            id: 'c',
            text: 'Flexible and adaptive',
            mbtiWeight: { E: 1, I: 0, S: 0, N: 1, T: 0, F: 0, J: 0, P: 1 }
          },
          {
            id: 'd',
            text: 'Structured and methodical',
            mbtiWeight: { E: 0, I: 1, S: 1, N: 0, T: 1, F: 0, J: 1, P: 0 }
          }
        ],
        category: 'lifestyle'
      }
    ];

    // Comprehensive category questions (general personality)
    const comprehensiveQuestions: QuestionCard[] = [
      {
        id: 'comp-1',
        question: 'When making important decisions, you:',
        options: [
          {
            id: 'a',
            text: 'Trust your instincts and feelings',
            mbtiWeight: { E: 0, I: 0, S: 0, N: 1, T: 0, F: 1, J: 0, P: 0 }
          },
          {
            id: 'b',
            text: 'Analyze facts and logical outcomes',
            mbtiWeight: { E: 0, I: 0, S: 1, N: 0, T: 1, F: 0, J: 1, P: 0 }
          },
          {
            id: 'c',
            text: 'Consider how it affects others',
            mbtiWeight: { E: 1, I: 0, S: 0, N: 0, T: 0, F: 1, J: 0, P: 0 }
          },
          {
            id: 'd',
            text: 'Weigh pros and cons carefully',
            mbtiWeight: { E: 0, I: 1, S: 1, N: 0, T: 1, F: 0, J: 1, P: 0 }
          }
        ],
        category: 'comprehensive'
      },
      {
        id: 'comp-2',
        question: 'In social situations, you tend to:',
        options: [
          {
            id: 'a',
            text: 'Be the center of attention',
            mbtiWeight: { E: 1, I: 0, S: 1, N: 0, T: 0, F: 1, J: 0, P: 1 }
          },
          {
            id: 'b',
            text: 'Observe and listen more than speak',
            mbtiWeight: { E: 0, I: 1, S: 0, N: 1, T: 1, F: 0, J: 1, P: 0 }
          },
          {
            id: 'c',
            text: 'Engage in deep one-on-one conversations',
            mbtiWeight: { E: 0, I: 1, S: 0, N: 1, T: 0, F: 1, J: 0, P: 0 }
          },
          {
            id: 'd',
            text: 'Adapt to the group dynamic',
            mbtiWeight: { E: 1, I: 0, S: 1, N: 0, T: 0, F: 1, J: 0, P: 1 }
          }
        ],
        category: 'comprehensive'
      },
      {
        id: 'comp-3',
        question: 'When learning something new, you prefer:',
        options: [
          {
            id: 'a',
            text: 'Hands-on practice and experimentation',
            mbtiWeight: { E: 1, I: 0, S: 1, N: 0, T: 1, F: 0, J: 0, P: 1 }
          },
          {
            id: 'b',
            text: 'Reading and theoretical understanding first',
            mbtiWeight: { E: 0, I: 1, S: 0, N: 1, T: 1, F: 0, J: 1, P: 0 }
          },
          {
            id: 'c',
            text: 'Learning with others in a group',
            mbtiWeight: { E: 1, I: 0, S: 1, N: 0, T: 0, F: 1, J: 0, P: 0 }
          },
          {
            id: 'd',
            text: 'Self-paced exploration',
            mbtiWeight: { E: 0, I: 1, S: 0, N: 1, T: 0, F: 1, J: 1, P: 0 }
          }
        ],
        category: 'comprehensive'
      },
      {
        id: 'comp-4',
        question: 'Your ideal partner would be someone who:',
        options: [
          {
            id: 'a',
            text: 'Shares your values and goals',
            mbtiWeight: { E: 0, I: 1, S: 1, N: 0, T: 1, F: 0, J: 1, P: 0 }
          },
          {
            id: 'b',
            text: 'Brings excitement and spontaneity',
            mbtiWeight: { E: 1, I: 0, S: 0, N: 1, T: 0, F: 1, J: 0, P: 1 }
          },
          {
            id: 'c',
            text: 'Understands your emotional needs',
            mbtiWeight: { E: 0, I: 1, S: 0, N: 1, T: 0, F: 1, J: 0, P: 0 }
          },
          {
            id: 'd',
            text: 'Challenges you intellectually',
            mbtiWeight: { E: 1, I: 1, S: 0, N: 1, T: 1, F: 0, J: 0, P: 0 }
          }
        ],
        category: 'comprehensive'
      },
      {
        id: 'comp-5',
        question: 'When facing conflict, you:',
        options: [
          {
            id: 'a',
            text: 'Address it directly and immediately',
            mbtiWeight: { E: 1, I: 0, S: 1, N: 0, T: 1, F: 0, J: 1, P: 0 }
          },
          {
            id: 'b',
            text: 'Take time to process before responding',
            mbtiWeight: { E: 0, I: 1, S: 0, N: 1, T: 0, F: 1, J: 1, P: 0 }
          },
          {
            id: 'c',
            text: 'Focus on finding a compromise',
            mbtiWeight: { E: 1, I: 0, S: 1, N: 0, T: 0, F: 1, J: 0, P: 0 }
          },
          {
            id: 'd',
            text: 'Look for the underlying meaning',
            mbtiWeight: { E: 0, I: 1, S: 0, N: 1, T: 0, F: 1, J: 0, P: 1 }
          }
        ],
        category: 'comprehensive'
      }
    ];

    // Relationships category questions
    const relationshipsQuestions: QuestionCard[] = [
      {
        id: 'relationships-1',
        question: 'In a relationship, you prioritize:',
        options: [
          {
            id: 'a',
            text: 'Emotional intimacy and connection',
            mbtiWeight: { E: 0, I: 0, S: 0, N: 1, T: 0, F: 1, J: 0, P: 0 }
          },
          {
            id: 'b',
            text: 'Shared activities and experiences',
            mbtiWeight: { E: 1, I: 0, S: 1, N: 0, T: 0, F: 0, J: 0, P: 1 }
          },
          {
            id: 'c',
            text: 'Stability and security',
            mbtiWeight: { E: 0, I: 1, S: 1, N: 0, T: 1, F: 0, J: 1, P: 0 }
          },
          {
            id: 'd',
            text: 'Growth and personal development',
            mbtiWeight: { E: 1, I: 1, S: 0, N: 1, T: 1, F: 0, J: 0, P: 0 }
          }
        ],
        category: 'relationships'
      },
      {
        id: 'relationships-2',
        question: 'Your love language is:',
        options: [
          {
            id: 'a',
            text: 'Quality time and deep conversations',
            mbtiWeight: { E: 0, I: 1, S: 0, N: 1, T: 0, F: 1, J: 0, P: 0 }
          },
          {
            id: 'b',
            text: 'Physical touch and affection',
            mbtiWeight: { E: 1, I: 0, S: 1, N: 0, T: 0, F: 1, J: 0, P: 0 }
          },
          {
            id: 'c',
            text: 'Acts of service and support',
            mbtiWeight: { E: 0, I: 1, S: 1, N: 0, T: 1, F: 0, J: 1, P: 0 }
          },
          {
            id: 'd',
            text: 'Words of affirmation and appreciation',
            mbtiWeight: { E: 1, I: 0, S: 0, N: 1, T: 0, F: 1, J: 0, P: 0 }
          }
        ],
        category: 'relationships'
      }
    ];

    // Values category questions
    const valuesQuestions: QuestionCard[] = [
      {
        id: 'values-1',
        question: 'What matters most to you in life?',
        options: [
          {
            id: 'a',
            text: 'Personal growth and self-discovery',
            mbtiWeight: { E: 0, I: 1, S: 0, N: 1, T: 0, F: 1, J: 1, P: 0 }
          },
          {
            id: 'b',
            text: 'Meaningful relationships and connections',
            mbtiWeight: { E: 1, I: 0, S: 0, N: 1, T: 0, F: 1, J: 0, P: 0 }
          },
          {
            id: 'c',
            text: 'Achievement and success',
            mbtiWeight: { E: 1, I: 1, S: 1, N: 0, T: 1, F: 0, J: 1, P: 0 }
          },
          {
            id: 'd',
            text: 'Freedom and independence',
            mbtiWeight: { E: 1, I: 1, S: 0, N: 1, T: 0, F: 0, J: 0, P: 1 }
          }
        ],
        category: 'values'
      }
    ];

    // Social category questions
    const socialQuestions: QuestionCard[] = [
      {
        id: 'social-1',
        question: 'At a party, you are most likely to:',
        options: [
          {
            id: 'a',
            text: 'Mingle with many different people',
            mbtiWeight: { E: 1, I: 0, S: 1, N: 0, T: 0, F: 1, J: 0, P: 1 }
          },
          {
            id: 'b',
            text: 'Have deep conversations with a few people',
            mbtiWeight: { E: 0, I: 1, S: 0, N: 1, T: 0, F: 1, J: 0, P: 0 }
          },
          {
            id: 'c',
            text: 'Observe and enjoy the atmosphere',
            mbtiWeight: { E: 0, I: 1, S: 1, N: 0, T: 1, F: 0, J: 1, P: 0 }
          },
          {
            id: 'd',
            text: 'Leave early to recharge',
            mbtiWeight: { E: 0, I: 1, S: 0, N: 1, T: 0, F: 0, J: 1, P: 0 }
          }
        ],
        category: 'social'
      }
    ];

    return [
      ...datingQuestions,
      ...lifestyleQuestions,
      ...comprehensiveQuestions,
      ...relationshipsQuestions,
      ...valuesQuestions,
      ...socialQuestions
    ];
  }

  // Get question count for a category
  static getQuestionCount(category: string): number {
    const allQuestions = this.getAllQuestions();
    return allQuestions.filter(q => q.category === category).length;
  }
}

